import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { preClientService } from '@/features/onboarding/services/preClient.service';
import type {
  PreClientQueryParams,
  PreClientListResponse,
  RejectedClientQueryParams,
  ApprovedClientQueryParams,
  PreClientDetail,
  DocumentUrlResponse,
} from '@/features/onboarding/types/preClient.types';

/**
 * Query key factory for pre-clients
 */
export const preClientKeys = {
  all: ['preClients'] as const,
  lists: () => [...preClientKeys.all, 'list'] as const,
  preAnalysisList: (params: PreClientQueryParams) =>
    [...preClientKeys.lists(), 'preAnalysis', params] as const,
  rejectedList: (params: RejectedClientQueryParams) =>
    [...preClientKeys.lists(), 'rejected', params] as const,
  approvedList: (params: ApprovedClientQueryParams) =>
    [...preClientKeys.lists(), 'approved', params] as const,
  details: () => [...preClientKeys.all, 'detail'] as const,
  detail: (id: string) => [...preClientKeys.details(), id] as const,
};

/**
 * Default pagination values
 */
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

/**
 * Hook to fetch pre-clients in analysis (status = 3)
 * Used exclusively for the Pre-Analysis page
 */
export const usePreAnalysisClients = (params: PreClientQueryParams = {}) => {
  const queryParams: PreClientQueryParams = {
    page: params.page ?? DEFAULT_PAGE,
    limit: params.limit ?? DEFAULT_LIMIT,
    ...params,
  };

  return useQuery<PreClientListResponse>({
    queryKey: preClientKeys.preAnalysisList(queryParams),
    queryFn: () => preClientService.getPreAnalysisList(queryParams),
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch rejected pre-clients (status = 1)
 * Used exclusively for the Rejected page
 */
export const useRejectedClients = (params: RejectedClientQueryParams = {}) => {
  const queryParams: RejectedClientQueryParams = {
    page: params.page ?? DEFAULT_PAGE,
    limit: params.limit ?? DEFAULT_LIMIT,
    ...params,
  };

  return useQuery<PreClientListResponse>({
    queryKey: preClientKeys.rejectedList(queryParams),
    queryFn: () => preClientService.getRejectedList(queryParams),
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch approved pre-clients (status = 2)
 * Used exclusively for the Approved page
 */
export const useApprovedClients = (params: ApprovedClientQueryParams = {}) => {
  const queryParams: ApprovedClientQueryParams = {
    page: params.page ?? DEFAULT_PAGE,
    limit: params.limit ?? DEFAULT_LIMIT,
    ...params,
  };

  return useQuery<PreClientListResponse>({
    queryKey: preClientKeys.approvedList(queryParams),
    queryFn: () => preClientService.getApprovedList(queryParams),
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to invalidate pre-analysis cache
 */
export const useInvalidatePreAnalysis = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: preClientKeys.lists() });
  }, [queryClient]);
};

/**
 * Hook to prefetch next page of pre-analysis clients
 */
export const usePrefetchPreAnalysis = (currentParams: PreClientQueryParams) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const nextPageParams: PreClientQueryParams = {
      ...currentParams,
      page: (currentParams.page ?? DEFAULT_PAGE) + 1,
    };

    void queryClient.prefetchQuery({
      queryKey: preClientKeys.preAnalysisList(nextPageParams),
      queryFn: () => preClientService.getPreAnalysisList(nextPageParams),
      staleTime: 30 * 1000,
    });
  }, [queryClient, currentParams]);
};

/**
 * Hook to prefetch next page of rejected clients
 */
export const usePrefetchRejected = (currentParams: RejectedClientQueryParams) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const nextPageParams: RejectedClientQueryParams = {
      ...currentParams,
      page: (currentParams.page ?? DEFAULT_PAGE) + 1,
    };

    void queryClient.prefetchQuery({
      queryKey: preClientKeys.rejectedList(nextPageParams),
      queryFn: () => preClientService.getRejectedList(nextPageParams),
      staleTime: 30 * 1000,
    });
  }, [queryClient, currentParams]);
};

/**
 * Hook to prefetch next page of approved clients
 */
export const usePrefetchApproved = (currentParams: ApprovedClientQueryParams) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const nextPageParams: ApprovedClientQueryParams = {
      ...currentParams,
      page: (currentParams.page ?? DEFAULT_PAGE) + 1,
    };

    void queryClient.prefetchQuery({
      queryKey: preClientKeys.approvedList(nextPageParams),
      queryFn: () => preClientService.getApprovedList(nextPageParams),
      staleTime: 30 * 1000,
    });
  }, [queryClient, currentParams]);
};

/**
 * Hook to fetch rejection categories
 * Used for filter dropdown in rejected clients page
 */
export const useRejectionCategories = () => {
  return useQuery<string[]>({
    queryKey: ['rejection-categories'],
    queryFn: () => preClientService.getRejectionCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes - categories rarely change
  });
};

/**
 * Hook to fetch pre-client details by ID
 * Works for all statuses (rejected, approved, in analysis)
 * @param id - Pre-client ID
 * @param options - Additional query options
 */
export const usePreClientDetail = (id: string | undefined, options?: { enabled?: boolean }) => {
  const clientId = id ?? '';

  return useQuery<PreClientDetail>({
    queryKey: preClientKeys.detail(clientId),
    queryFn: () => preClientService.getById(clientId),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to invalidate pre-client detail cache
 */
export const useInvalidatePreClientDetail = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (id?: string) => {
      if (id) {
        void queryClient.invalidateQueries({ queryKey: preClientKeys.detail(id) });
      } else {
        void queryClient.invalidateQueries({ queryKey: preClientKeys.details() });
      }
    },
    [queryClient]
  );
};

/**
 * Hook to prefetch pre-client detail
 */
export const usePrefetchPreClientDetail = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (id: string) => {
      void queryClient.prefetchQuery({
        queryKey: preClientKeys.detail(id),
        queryFn: () => preClientService.getById(id),
        staleTime: 60 * 1000,
      });
    },
    [queryClient]
  );
};

/**
 * Hook to fetch document URL
 * Returns a function to fetch the URL on demand (not automatically)
 * Used when user clicks on a document to view/download
 */
export const useDocumentUrl = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocumentUrl = useCallback(
    async (documentId: string): Promise<DocumentUrlResponse | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await preClientService.getDocumentUrl(documentId);
        return response;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Erro ao buscar URL do documento');
        setError(errorObj);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    fetchDocumentUrl,
    isLoading,
    error,
  };
};
